import {
    Button, Col, Row, Space, Typography, notification, theme,
} from 'antd';
import type { FC } from 'react';
import { useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { CopyOutlined, DownloadOutlined } from '@ant-design/icons';

const { Text } = Typography;
const { useToken } = theme;

interface IBackupCodesDisplayProps {
	codes: string[];
	onDone: () => void;
}

const BackupCodesDisplay: FC<IBackupCodesDisplayProps> = ({ codes, onDone }) => {
    const { t } = useTranslation('login');
    const tRef = useRef(t);
    tRef.current = t;
    const { token } = useToken();

    const handleCopy = useCallback(async () => {
        const text = codes.join('\n');
        await navigator.clipboard.writeText(text);
        notification.success({ message: tRef.current('SETUP_2FA.BACKUP_COPIED'), duration: 2 });
    }, [codes]);

    const handleDownload = useCallback(() => {
        const text = codes.join('\n');
        const blob = new Blob([`Picker - Backup Codes\n${'='.repeat(30)}\n\n${text}\n`], { type: 'text/plain' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'picker-backup-codes.txt';
        a.click();
        URL.revokeObjectURL(url);
    }, [codes]);

    return (
        <Space direction="vertical" size={16} style={{ width: '100%' }}>
            <Text type="secondary" style={{ textAlign: 'center', display: 'block' }}>
                {t('SETUP_2FA.BACKUP_DESCRIPTION')}
            </Text>

            <div style={{
                background: token.colorFillTertiary, borderRadius: 8, padding: 16, fontFamily: 'monospace', fontSize: 14,
            }}
            >
                <Row gutter={[8, 8]} justify="center">
                    {codes.map((code) => (
                        <Col key={code}>
                            <div style={{
                                background: token.colorBgContainer,
                                border: `1px solid ${token.colorBorder}`,
                                borderRadius: 4,
                                padding: '4px 12px',
                                minWidth: 120,
                                textAlign: 'center',
                            }}
                            >
                                {code}
                            </div>
                        </Col>
                    ))}
                </Row>
            </div>

            <Row gutter={8}>
                <Col span={12}>
                    <Button icon={<CopyOutlined />} onClick={handleCopy} block>{t('SETUP_2FA.COPY')}</Button>
                </Col>
                <Col span={12}>
                    <Button icon={<DownloadOutlined />} onClick={handleDownload} block>{t('SETUP_2FA.DOWNLOAD')}</Button>
                </Col>
            </Row>

            <Button type="primary" block size="large" onClick={onDone}>
                {t('SETUP_2FA.DONE')}
            </Button>
        </Space>
    );
};

export default BackupCodesDisplay;
